import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Separator } from './ui/separator';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api.service';
import { Activity } from '../types/activity';
import { CreatorProfileModal } from './CreatorProfileModal';
import { ApplicationModal } from './ApplicationModal';
import { ApplicationStatus } from '../types/application';
import { ParticipationStatus } from '../types/participation';
import {
    ArrowLeft,
    Calendar,
    MapPin,
    Users,
    Clock,
    Share2,
    Heart,
    HeartOff,
    DollarSign,
    Star,
    CheckCircle,
    ExternalLink,
    Copy,
    X
} from 'lucide-react';
import { CommentSection } from './CommentSection';


export function ActivityDetailPage() {
    const { id } = useParams<{ id: string }>();
    const { isAuthenticated, user, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [activity, setActivity] = useState<Activity | null>(null);
    const [activityDate, setActivityDate] = useState<string | null>(null);
    const [activityTime, setActivityTime] = useState<string | null>(null);
    const [applicationStatus, setApplicationStatus] = useState<ApplicationStatus>(ApplicationStatus.NONE);
    const [participationStatus, setParticipationStatus] = useState<ParticipationStatus | null>(null);
    const [isFollowing, setIsFollowing] = useState(false);
    const [numberOfParticipations, setNumberOfParticipations] = useState(0);
    const [participantAvatars, setParticipantAvatars] = useState<string[]>([]);
    const [selectedCreator, setSelectedCreator] = useState<string | null>(null);
    const [isCreatorModalOpen, setIsCreatorModalOpen] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [showApplicationModal, setShowApplicationModal] = useState(false);
    const [activities, setActivities] = useState<Activity[]>([]);

    const fetchActivity = async (id: string) => {
        const activity = await apiService.getActivityById(id)
        const date = new Date(activity.dateTime).toLocaleDateString();
        const time = new Date(activity.dateTime).toLocaleTimeString();
        setActivity(activity);
        setActivityDate(date);
        setActivityTime(time);

        const application = await apiService.getApplicationByUserIdActivityId(user!.id, activity.id);
        setApplicationStatus(application?.status || ApplicationStatus.NONE);

        const participation = await apiService.getParticipationByUserIdActivityId(user!.id, activity.id);
        setParticipationStatus(participation?.status);

        // Fetch other activities by the same creator after we get the activity
        if (activity.creatorId) {
            await fetchActivitiesByCreatorId(activity.creatorId);
            await getNumberOfParticipations(activity);
        }
    }

    const getNumberOfParticipations = async (activity: Activity) => {
        const participations = await apiService.getParticipationsByActivityId(activity.id);
        setNumberOfParticipations(participations.length)
        setParticipantAvatars(participations.map(participation => participation.user.avatar || ''));
    }

    const fetchActivitiesByCreatorId = async (creatorId: string) => {
        const activities = await apiService.getActivitiesByCreatorId(creatorId);
        setActivities(activities);
    }

    useEffect(() => {
        if (id) {
            fetchActivity(id);
        }
    }, [id]);

    if (!activity) {
        return (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-center py-12">
                    <h1 className="text-2xl font-bold mb-4">Activity Not Found</h1>
                    <p className="text-muted-foreground mb-6">
                        The activity you're looking for doesn't exist or has been removed.
                    </p>
                    <Button onClick={() => navigate('/explore')}>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Explore
                    </Button>
                </div>
            </div>
        );
    }

    const handleApplyToActivity = () => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }

        if (applicationStatus === ApplicationStatus.NONE) {
            setShowApplicationModal(true);
        }
    };

    const handleApplicationSubmit = async (activityId: string, note: string) => {
        if (isAuthenticated && user) {
            try {
                // before application system, join activity directly
                // await apiService.joinActivity(activity.id, user.id);

                // after application system, create pending application first
                // TODO: need to create the apply modal(do we need to?)
                const existingApplication = await apiService.getApplicationByUserIdActivityId(user.id, activityId);
                if (existingApplication && existingApplication.status === ApplicationStatus.WITHDRAWN) {
                    await apiService.updateApplication(existingApplication.id, ApplicationStatus.PENDING);
                } else {
                    await apiService.createApplication({
                        activityId: activityId,
                        userId: user.id,
                        status: ApplicationStatus.PENDING,
                        note: note
                    });
                }
                // Refresh participation count after joining
                await getNumberOfParticipations(activity);
                toast.success('Application submitted successfully');
                // setIsFlipped(true);
            } catch (error) {
                // console.error('Error joining activity:', error);
                toast.error('You have already applied to this activity');
            }
        }
        setShowApplicationModal(false);
    };

    const handleCreatorClick = () => {
        setSelectedCreator(activity.creator?.name || '');
        setIsCreatorModalOpen(true);
    };

    const handleFollowChange = (creatorName: string, following: boolean) => {
        setIsFollowing(following);
    };

    const handleShare = async () => {
        const url = window.location.href;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: activity.title,
                    text: `Check out this ${activity.type} activity: ${activity.title}`,
                    url: url,
                });
            } catch (err) {
                // Fallback to clipboard
                handleCopyUrl();
            }
        } else {
            handleCopyUrl();
        }
    };

    const handleCopyUrl = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            // Fallback for older browsers
            console.log('Copy failed');
        }
    };

    const spotsLeft = activity.maxParticipants - activity.participants;
    const isAlmostFull = spotsLeft <= 3;

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Back Button */}
            <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="mb-6 flex items-center space-x-2"
            >
                <ArrowLeft size={16} />
                <span>Back</span>
            </Button>

            {/* Hero Section */}
            <div className="relative mb-8 rounded-xl overflow-hidden">
                <img
                    src={activity.image}
                    alt={activity.title}
                    className="w-full h-64 md:h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                    <div className="flex items-start justify-between">
                        <div>
                            <Badge className="mb-2" variant="secondary">
                                {activity.type}
                            </Badge>
                            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                                {activity.title}
                            </h1>
                            <div className="flex items-center space-x-4 text-white/90 text-sm">
                                <div className="flex items-center space-x-1">
                                    <Calendar size={16} />
                                    <span>{activityDate}</span>
                                </div>
                                <div className="flex items-center space-x-1">
                                    <Clock size={16} />
                                    <span>{activityTime}</span>
                                </div>
                            </div>
                        </div>
                        <Button
                            onClick={handleShare}
                            variant="secondary"
                            size="sm"
                            className="flex items-center space-x-2"
                        >
                            {copySuccess ? (
                                <>
                                    <CheckCircle size={16} />
                                    <span>Copied!</span>
                                </>
                            ) : (
                                <>
                                    <Share2 size={16} />
                                    <span>Share</span>
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Activity Details */}
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="text-xl font-semibold mb-4">About This Activity</h2>
                            <p className="text-muted-foreground leading-relaxed mb-6">
                                {activity.description}
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                                        <MapPin className="w-5 h-5 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <p className="font-medium">Location</p>
                                        <p className="text-sm text-muted-foreground">{activity.location?.name.split(',')[0]}</p>
                                    </div>
                                </div>

                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                                        <Calendar className="w-5 h-5 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <p className="font-medium">Date & Time</p>
                                        <p className="text-sm text-muted-foreground">
                                            {activityDate} at {activityTime}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                                        <Users className="w-5 h-5 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <p className="font-medium">Participants</p>
                                        <p className="text-sm text-muted-foreground">
                                            {numberOfParticipations} of {activity.maxParticipants} joined
                                            {spotsLeft > 0 && (
                                                <span className={isAlmostFull ? "text-orange-600" : "text-green-600"}>
                                                    {" "}({spotsLeft} spots left)
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                                        <DollarSign className="w-5 h-5 text-muted-foreground" />
                                    </div>
                                    <div>
                                        <p className="font-medium">Price</p>
                                        <p className="text-sm text-muted-foreground">
                                            {activity.price === 0 ? 'Free' : `$${activity.price}`}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Participants */}
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="text-xl font-semibold mb-4">Participants</h2>
                            <div className="flex flex-wrap gap-2">
                                {participantAvatars.map(avatar => (
                                    <Avatar className="w-10 h-10">
                                        <AvatarImage src={avatar} alt="Participant" />
                                        <AvatarFallback>{"JD".slice(0, 2).toUpperCase()}</AvatarFallback>
                                    </Avatar>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Creator Info */}
                    <Card>
                        <CardContent className="p-6">
                            <h2 className="text-xl font-semibold mb-4">Creator</h2>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <Avatar className="w-12 h-12">
                                        <AvatarImage src={activity.creator?.avatar} alt={activity.creator?.name} />
                                        <AvatarFallback>{activity.creator?.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                        <button
                                            onClick={handleCreatorClick}
                                            className="text-lg font-medium text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded-sm"
                                        >
                                            {activity.creator?.name}
                                        </button>
                                        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                            <span>4.8 rating</span>
                                            <span>•</span>
                                            <span>{activities.length} activities</span>
                                        </div>
                                    </div>
                                </div>

                                {isAuthenticated && (
                                    <Button
                                        variant={isFollowing ? "secondary" : "outline"}
                                        size="sm"
                                        onClick={() => setIsFollowing(!isFollowing)}
                                        className="flex items-center space-x-2"
                                    >
                                        {isFollowing ? (
                                            <>
                                                <Heart className="w-4 h-4 fill-current" />
                                                <span>Following</span>
                                            </>
                                        ) : (
                                            <>
                                                <HeartOff className="w-4 h-4" />
                                                <span>Follow</span>
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Comment Section - Only visible to approved participants */}
                    {(isAuthenticated && participationStatus === ParticipationStatus.JOINED || isAdmin) && (
                        <CommentSection entityId={activity.id} currentUserId={user?.id} creatorId={activity.creator?.id} />
                    )}

                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Join Card */}
                    <Card>
                        <CardContent className="p-6">
                            <div className="text-center space-y-4">
                                <div>
                                    <p className="text-2xl font-bold">
                                        {activity.price === 0 ? 'Free' : `$${activity.price}`}
                                    </p>
                                    <p className="text-sm text-muted-foreground">per person</p>
                                </div>

                                <Separator />

                                <div className="space-y-2">
                                    <Badge variant="outline" className="w-full justify-center py-2">
                                        {activity.difficulty} Level
                                    </Badge>

                                    {spotsLeft === 0 ? (
                                        <Button disabled className="w-full">
                                            Activity Full
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={handleApplyToActivity}
                                            className="w-full"
                                            variant={applicationStatus === ApplicationStatus.APPROVED ? "secondary" :
                                                applicationStatus === ApplicationStatus.PENDING ? "outline" :
                                                    applicationStatus === ApplicationStatus.REJECTED ? "destructive" : "default"}
                                            disabled={applicationStatus === ApplicationStatus.REJECTED}
                                        >
                                            {applicationStatus === ApplicationStatus.APPROVED ? (
                                                <>
                                                    <CheckCircle className="w-4 h-4 mr-2" />
                                                    Approved
                                                </>
                                            ) : applicationStatus === ApplicationStatus.PENDING ? (
                                                <>
                                                    <Clock className="w-4 h-4 mr-2" />
                                                    Application Pending
                                                </>
                                            ) : applicationStatus === ApplicationStatus.REJECTED ? (
                                                <>
                                                    <X className="w-4 h-4 mr-2" />
                                                    Application Declined
                                                </>
                                            ) : isAuthenticated ? (
                                                'Apply to Join'
                                            ) : (
                                                'Login to Apply'
                                            )}
                                        </Button>
                                    )}
                                </div>

                                {isAlmostFull && spotsLeft > 0 && (
                                    <div className="bg-orange-50 p-3 rounded-lg">
                                        <p className="text-sm text-orange-800">
                                            ⚡ Only {spotsLeft} spots left! Join now to secure your place.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Quick Share */}
                    <Card>
                        <CardContent className="p-6">
                            <h3 className="font-semibold mb-3">Share This Activity</h3>
                            <div className="space-y-2">
                                <Button
                                    onClick={handleShare}
                                    variant="outline"
                                    className="w-full flex items-center justify-center space-x-2"
                                >
                                    <Share2 size={16} />
                                    <span>Share Activity</span>
                                </Button>
                                <Button
                                    onClick={handleCopyUrl}
                                    variant="ghost"
                                    className="w-full flex items-center justify-center space-x-2"
                                >
                                    <Copy size={16} />
                                    <span>{copySuccess ? 'Copied!' : 'Copy Link'}</span>
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Organizer Profile Modal */}
            {/* {selectedOrganizer && (
                <OrganizerProfileModal
                    isOpen={isOrganizerModalOpen}
                    onClose={() => {
                        setIsOrganizerModalOpen(false);
                        setSelectedOrganizer(null);
                    }}
                    organizerName={selectedOrganizer}
                    onFollowChange={handleFollowChange}
                />
            )} */}

            {/* Application Modal */}
            <ApplicationModal
                isOpen={showApplicationModal}
                onClose={() => setShowApplicationModal(false)}
                activity={activity}
                onSubmit={handleApplicationSubmit}
            />
        </div>
    );
}